const PAGE_CONTROL_SELECTOR = [
  '.ant-pagination-item',
  '.ant-pagination-prev',
  '.ant-pagination-next',
  '.ant-pagination-jump-prev',
  '.ant-pagination-jump-next',
].join(', ');

const LIST_SELECTOR = '.ant-table-wrapper, .ant-list';
const OVERLAY_SELECTOR = '.ant-modal-wrap, .ant-drawer';

/** Space left above a list that is scrolled to directly. */
const LIST_TOP_GAP = 12;

/**
 * Spread on a container to keep its lists in place when they are paged: only the start of
 * the list is brought on screen. For a table at the bottom of a dashboard, where the top
 * of the page is screens of charts away.
 */
export const pagingKeepsPlace = { 'data-paging-keeps-place': true } as const;

const isDisabled = (el: Element) =>
  el.classList.contains('ant-pagination-disabled') ||
  el.getAttribute('aria-disabled') === 'true';

/**
 * Pagination control that triggered a real page change, or null when the click should
 * not scroll (current page, disabled control, opening the page-size dropdown).
 */
export function paginationScrollOrigin(
  target: EventTarget | null,
): Element | null {
  if (!(target instanceof Element)) return null;

  const control = target.closest(PAGE_CONTROL_SELECTOR);
  if (control) {
    if (
      isDisabled(control) ||
      control.classList.contains('ant-pagination-item-active')
    ) {
      return null;
    }
    return control;
  }

  const option = target.closest('.ant-select-item-option');
  if (
    !option ||
    option.classList.contains('ant-select-item-option-selected') ||
    isDisabled(option)
  ) {
    return null;
  }
  return document.querySelector(
    '.ant-pagination-options-size-changer.ant-select-open',
  );
}

/**
 * The list a pagination control pages, innermost first, followed by its ancestors.
 * Capture this while handling the event: a page that swaps its table for a spinner
 * detaches the list, and the nearest ancestor still on the page then stands in for it.
 */
export function paginatedListPath(control: Element): Element[] {
  const path: Element[] = [];
  let el: Element | null =
    control.closest(LIST_SELECTOR) ??
    control.closest('.ant-pagination') ??
    control;
  while (el) {
    path.push(el);
    el = el.parentElement;
  }
  return path;
}

const isVerticalScroller = (el: HTMLElement) => {
  const { overflowY } = window.getComputedStyle(el);
  if (overflowY !== 'auto' && overflowY !== 'scroll' && overflowY !== 'overlay')
    return false;
  return el.scrollHeight - el.clientHeight > 1;
};

const isRendered = (el: Element) => el.getClientRects().length > 0;

/**
 * A widget on the screen rather than the list the screen is about: a table whose
 * fixed-height body scrolls inside itself, or one marked with `pagingKeepsPlace`.
 */
const staysInPlace = (list: Element) => {
  if (list.closest('[data-paging-keeps-place]')) return true;
  const body = list.querySelector('.ant-table-body');
  if (!body) return false;
  const { overflowY } = window.getComputedStyle(body);
  return overflowY === 'auto' || overflowY === 'scroll';
};

/**
 * Whether `list` is the list its screen (page, modal or drawer) is about: the first paged
 * list there that grows with the screen. Deliberately not a matter of where the list sits
 * or how tall the window is — the same screen must behave the same on every monitor.
 */
const isMainList = (list: Element, overlay: Element | null) => {
  if (staysInPlace(list)) return false;
  const scope: ParentNode = overlay ?? document;
  for (const other of Array.from(scope.querySelectorAll(LIST_SELECTOR))) {
    const precedes =
      other !== list &&
      (other.compareDocumentPosition(list) &
        Node.DOCUMENT_POSITION_FOLLOWING) !==
        0;
    if (!precedes) break;
    // A list in an open modal is not part of the page behind it.
    if (!overlay && other.closest(OVERLAY_SELECTOR)) continue;
    if (
      isRendered(other) &&
      !staysInPlace(other) &&
      other.querySelector('.ant-pagination')
    ) {
      return false;
    }
  }
  return true;
};

/** Scroll `scroller` up until the start of the list is on screen. Never scrolls down. */
const showListStart = (
  scroller: HTMLElement,
  viewportTop: number,
  list: Element,
) => {
  const listTop =
    list.getBoundingClientRect().top - viewportTop + scroller.scrollTop;
  const target = Math.max(0, listTop - LIST_TOP_GAP);
  if (scroller.scrollTop > target) scroller.scrollTop = target;
};

/**
 * Move the view after the user paged `list`.
 * The screen's main list goes to the very top, exactly as when the screen was opened.
 * Any other list (a second table further down, a table that scrolls inside itself, one
 * marked `pagingKeepsPlace`) only gets its own start shown — the top of the page would
 * take it off screen.
 * A list inside a modal or drawer only scrolls that overlay, so the page behind stays put.
 * (A table with a fixed-height body rewinds that body itself — antd does it on every change.)
 */
export function scrollAfterPageChange(list: Element): void {
  const overlay = list.closest(OVERLAY_SELECTOR);
  const toTop = isMainList(list, overlay);
  const move = (scroller: HTMLElement, viewportTop: number) => {
    if (toTop) scroller.scrollTop = 0;
    else showListStart(scroller, viewportTop, list);
  };

  const stopAt = overlay ? overlay.parentElement : document.body;
  for (
    let el = list.parentElement;
    el && el !== stopAt && el !== document.documentElement;
    el = el.parentElement
  ) {
    if (isVerticalScroller(el)) {
      move(el, el.getBoundingClientRect().top + el.clientTop);
    }
  }
  if (overlay) return;

  move((document.scrollingElement ?? document.documentElement) as HTMLElement, 0);
}
